// frontend/src/pages/Store/ShopPage.js
// Shop V1 — Top-level shop page. Renders under appMode === 'storePage'.
// Owns all shop state. No audio — deferred to V2 per approved decisions.

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../auth/AuthContext';
import { Button, Checkbox } from '../../components/ui';
import { getShopState, openShopOffer, mockPurchaseMythril, ShopFeatureDisabledError } from '../../api/shopApi';
import { PULL_LADDER, VARIANT_PACK_COST } from '../../data/shopData';
import ShopItemCard from '../../components/shop/ShopItemCard';
import PunchCard from '../../components/shop/PunchCard';
import CelebrationOverlay from '../../components/shop/CelebrationOverlay';
import MythrilPurchaseModal from '../../components/shop/MythrilPurchaseModal';
import './ShopPage.css';

// ---------------------------------------------------------------------------
// computeOfferCost — Fix 7B: derive cost directly from offer, not mock balance delta
// ---------------------------------------------------------------------------

/**
 * Compute the Mythril cost for a given offer based on current shop state.
 * Returns 0 for free pulls (daily free or punch credit).
 * This is the authoritative cost source for AuthContext balance deduction.
 *
 * @param {'printer'|'variantPack'} offerKey
 * @param {object|null} shopState — current shop state from getShopState()
 * @returns {number} Mythril cost (0 = free)
 */
function computeOfferCost(offerKey, shopState) {
    if (!shopState) return 0;
    if (offerKey === 'printer') {
        if (shopState.hasFreeAvailable) return 0; // free pull (daily or punch credit)
        // Fix 1: prefer backend next_price; fall back to ladder constant only when absent
        if (shopState.printerNextPrice != null) return shopState.printerNextPrice;
        // PULL_LADDER[0] is the free slot; paid pulls start at index 1.
        // pullsUsedToday counts paid pulls already used (0 = none yet → first paid = PULL_LADDER[1]).
        const idx = Math.min((shopState.pullsUsedToday || 0) + 1, PULL_LADDER.length - 1);
        return PULL_LADDER[idx];
    }
    if (offerKey === 'variantPack') {
        // Fix 1: prefer backend next_price; fall back to constant only when absent
        if (shopState.packNextPrice != null) return shopState.packNextPrice;
        return VARIANT_PACK_COST;
    }
    return 0;
}

// ---------------------------------------------------------------------------
// Countdown timer helper — pure function, no side effects
// ---------------------------------------------------------------------------

/**
 * Compute seconds remaining until midnight Pacific Time (America/Los_Angeles).
 * Uses Intl.DateTimeFormat to find the current Pacific time, then calculates
 * the delta to the next midnight.
 * @returns {number} seconds until next Pacific midnight
 */
function secondsUntilMidnightPacific() {
    const now = new Date();
    // Get current Pacific time components
    const pacificParts = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/Los_Angeles',
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
        hour12: false,
    }).formatToParts(now);

    const get = (type) => parseInt(pacificParts.find(p => p.type === type)?.value || '0', 10);
    const hours = get('hour');
    const minutes = get('minute');
    const seconds = get('second');

    const secondsElapsed = hours * 3600 + minutes * 60 + seconds;
    const secondsInDay = 24 * 3600;
    return secondsInDay - secondsElapsed;
}

/**
 * Format seconds as HH:MM:SS.
 * @param {number} totalSeconds
 * @returns {string}
 */
function formatCountdown(totalSeconds) {
    const s = Math.max(0, Math.floor(totalSeconds));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return [h, m, sec].map(n => String(n).padStart(2, '0')).join(':');
}

// ---------------------------------------------------------------------------
// Pull cost helper
// ---------------------------------------------------------------------------

/**
 * Get the Mythril cost for the next pull given current shop state.
 * Returns 'FREE' if a free entitlement is available.
 * @param {object} shopState
 * @returns {number|'FREE'}
 */
function getNextPrinterCost(shopState) {
    if (!shopState) return 'FREE';
    if (shopState.hasFreeAvailable) return 'FREE';
    // Fix 1: prefer backend next_price; fall back to ladder constant only when absent
    if (shopState.printerNextPrice != null) return shopState.printerNextPrice;
    // PULL_LADDER[0] is the free slot; paid pulls start at index 1.
    const idx = Math.min((shopState.pullsUsedToday || 0) + 1, PULL_LADDER.length - 1);
    return PULL_LADDER[idx];
}

// ---------------------------------------------------------------------------
// ShopPage component
// ---------------------------------------------------------------------------

/**
 * ShopPage
 *
 * @param {object}   props
 * @param {object}   props.user     — authenticatedUser from AppRouter
 * @param {Function} props.setPage  — setAppModeWithPersistence from AppRouter
 * @param {Function} [props.onFreePullAvailable] — callback(bool) for nav dot
 */
function ShopPage({ user, setPage, onFreePullAvailable }) {
    if (!setPage) {
        throw new Error('ShopPage: setPage prop is required for navigation');
    }

    const { mythrilBalance, setMythrilBalance } = useAuth();
    if (typeof setMythrilBalance !== 'function') {
        throw new Error(`ShopPage requires AuthProvider.setMythrilBalance; received ${typeof setMythrilBalance}`);
    }

    // --- State ---
    const [shopState, setShopState] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [shopDisabled, setShopDisabled] = useState(false);
    const [celebrationOutcome, setCelebrationOutcome] = useState(null);
    const [showMythrilModal, setShowMythrilModal] = useState(false);
    // Fix 1: initialize quickBuy from localStorage on mount
    const [quickBuy, setQuickBuy] = useState(
        () => localStorage.getItem('mythic_shop_quick_buy') === 'true'
    );
    // confirmPending: null | { offerKey: string }
    const [confirmPending, setConfirmPending] = useState(null);
    const [countdown, setCountdown] = useState('');
    const [consecutivePulls, setConsecutivePulls] = useState(0);
    const [purchaseLoading, setPurchaseLoading] = useState(false);

    const intervalRef = useRef(null);
    // pendingRequestIdRef: holds the UUID for the current purchase intent.
    // Generated once per intent and reused on retry to ensure idempotency.
    // Cleared after success, non-retryable error, or cancel.
    const pendingRequestIdRef = useRef(null);

    // --- Load shop state on mount ---
    useEffect(() => {
        let cancelled = false;
        setIsLoading(true);
        setLoadError(null);
        getShopState()
            .then(state => {
                if (cancelled) return;
                setShopState(state);
                // Sync AuthContext balance from live backend on initial load
                if (typeof state._balance === 'number') {
                    setMythrilBalance(state._balance);
                }
            })
            .catch(err => {
                if (cancelled) return;
                if (err instanceof ShopFeatureDisabledError) {
                    setShopDisabled(true);
                } else {
                    setLoadError(err.message);
                }
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });
        return () => { cancelled = true; };
    }, [setMythrilBalance]);

    // --- Notify parent of free pull availability for nav dot ---
    useEffect(() => {
        if (typeof onFreePullAvailable === 'function' && shopState) {
            onFreePullAvailable(shopState.hasFreeAvailable);
        }
    }, [shopState, onFreePullAvailable]);

    // --- Countdown timer ---
    useEffect(() => {
        const tick = () => {
            setCountdown(formatCountdown(secondsUntilMidnightPacific()));
        };
        tick(); // immediate first render
        intervalRef.current = setInterval(tick, 1000);
        return () => clearInterval(intervalRef.current);
    }, []);

    // --- Quick Buy toggle (Fix 1: use e.target.checked from DOM event) ---
    const handleQuickBuyChange = useCallback((e) => {
        const checked = e.target.checked;
        setQuickBuy(checked);
        localStorage.setItem('mythic_shop_quick_buy', String(checked));
    }, []);

    // --- Mythril IAP purchase (Fix 3 + Fix 2) ---
    const handleMythrilPurchase = useCallback(async (mythrilAmount) => {
        try {
            await mockPurchaseMythril(mythrilAmount);
            setMythrilBalance(prev => prev + mythrilAmount);
        } catch (err) {
            alert(`Mythril purchase failed: ${err.message}`);
        }
    }, [setMythrilBalance]);

    // --- Purchase flow ---
    const handlePurchase = useCallback((offerKey) => {
        if (purchaseLoading) return;

        // Always show confirmation when Quick Buy is off — replaces any pending confirmation.
        if (!quickBuy) {
            setConfirmPending({ offerKey });
            return;
        }

        // Quick Buy ON — execute purchase immediately.
        // Compute cost BEFORE the offer is opened (shopState reflects pre-purchase state).
        const cost = computeOfferCost(offerKey, shopState);

        // Insufficient balance guard — block paid purchases when balance is too low
        if (cost > 0 && mythrilBalance < cost) {
            alert('Insufficient Mythril');
            setConfirmPending(null);
            return;
        }

        // Fix 3a: pendingRequestIdRef stores { requestId, offerKey } to prevent cross-offer UUID reuse.
        // If there's a pending intent for a DIFFERENT offer, abandon it and generate a new UUID.
        if (pendingRequestIdRef.current && pendingRequestIdRef.current.offerKey !== offerKey) {
            pendingRequestIdRef.current = null;
        }
        if (!pendingRequestIdRef.current) {
            pendingRequestIdRef.current = { requestId: crypto.randomUUID(), offerKey };
        }
        const requestId = pendingRequestIdRef.current.requestId;

        setPurchaseLoading(true);
        openShopOffer(offerKey, requestId)
            .then(outcome => {
                // Fix 2: clear after confirmed POST success — not in catch
                pendingRequestIdRef.current = null;
                // Use balance_after from server response — no local subtraction math.
                setMythrilBalance(outcome.receipt.balance_after);
                // replayed: true means the user already saw the celebration — skip it
                if (!outcome.replayed) {
                    setCelebrationOutcome(outcome);
                }
                setConfirmPending(null);
                // Track consecutive single pulls for lightweight mode
                if (!outcome.replayed && offerKey === 'printer') {
                    setConsecutivePulls(prev => prev + 1);
                } else if (!outcome.replayed) {
                    setConsecutivePulls(0);
                }
                // Fix 2: Refresh shop state — failure here is NOT a purchase failure.
                // Inner catch handles GET failure independently from the POST success path.
                getShopState()
                    .then(freshState => {
                        setShopState(freshState);
                    })
                    .catch(() => {
                        // GET failed after successful POST — soft error only.
                        // The purchase succeeded; state may be stale — reload to recover.
                        console.warn('ShopPage: getShopState() failed after successful purchase — state may be stale. Reloading.');
                        window.location.reload();
                    });
            })
            .catch(err => {
                // Fix 2: This catch ONLY fires if openShopOffer() (the POST) itself failed.
                // Fix 3c: Read err.status directly (set by parseShopResponse) — not string parsing.
                // Only clear the request ID on definitive client errors (4xx).
                // On network failures or server errors (5xx), preserve the UUID so
                // a retry uses the same request_id and the backend can deduplicate.
                const isDefinitiveClientError = err.status === 400 || err.status === 401 ||
                    err.status === 403 || err.status === 404 || err.status === 422;
                if (isDefinitiveClientError) {
                    pendingRequestIdRef.current = null;
                }
                // Surface error to user — never silent-fail
                alert(`Purchase failed: ${err.message}`);
                setConfirmPending(null);
            })
            .finally(() => {
                setPurchaseLoading(false);
            });
    }, [quickBuy, purchaseLoading, shopState, mythrilBalance, setMythrilBalance]);

    const handleConfirm = useCallback(() => {
        if (!confirmPending) return;
        const { offerKey } = confirmPending;
        // Compute cost BEFORE the offer is opened (shopState reflects pre-purchase state).
        const cost = computeOfferCost(offerKey, shopState);

        // Insufficient balance guard — block paid purchases when balance is too low
        if (cost > 0 && mythrilBalance < cost) {
            alert('Insufficient Mythril');
            setConfirmPending(null);
            return;
        }

        // Fix 3a: pendingRequestIdRef stores { requestId, offerKey } to prevent cross-offer UUID reuse.
        // If there's a pending intent for a DIFFERENT offer, abandon it and generate a new UUID.
        if (pendingRequestIdRef.current && pendingRequestIdRef.current.offerKey !== offerKey) {
            pendingRequestIdRef.current = null;
        }
        if (!pendingRequestIdRef.current) {
            pendingRequestIdRef.current = { requestId: crypto.randomUUID(), offerKey };
        }
        const requestId = pendingRequestIdRef.current.requestId;

        setPurchaseLoading(true);
        openShopOffer(offerKey, requestId)
            .then(outcome => {
                // Fix 2: clear after confirmed POST success — not in catch
                pendingRequestIdRef.current = null;
                // Use balance_after from server response — no local subtraction math.
                setMythrilBalance(outcome.receipt.balance_after);
                // replayed: true means the user already saw the celebration — skip it
                if (!outcome.replayed) {
                    setCelebrationOutcome(outcome);
                }
                setConfirmPending(null);
                if (!outcome.replayed && offerKey === 'printer') {
                    setConsecutivePulls(prev => prev + 1);
                } else if (!outcome.replayed) {
                    setConsecutivePulls(0);
                }
                // Fix 2: Refresh shop state — failure here is NOT a purchase failure.
                // Inner catch handles GET failure independently from the POST success path.
                getShopState()
                    .then(freshState => {
                        setShopState(freshState);
                    })
                    .catch(() => {
                        // GET failed after successful POST — soft error only.
                        // The purchase succeeded; state may be stale — reload to recover.
                        console.warn('ShopPage: getShopState() failed after successful purchase — state may be stale. Reloading.');
                        window.location.reload();
                    });
            })
            .catch(err => {
                // Fix 2: This catch ONLY fires if openShopOffer() (the POST) itself failed.
                // Fix 3c: Read err.status directly (set by parseShopResponse) — not string parsing.
                // Only clear the request ID on definitive client errors (4xx).
                // On network failures or server errors (5xx), preserve the UUID so
                // a retry uses the same request_id and the backend can deduplicate.
                const isDefinitiveClientError = err.status === 400 || err.status === 401 ||
                    err.status === 403 || err.status === 404 || err.status === 422;
                if (isDefinitiveClientError) {
                    pendingRequestIdRef.current = null;
                }
                alert(`Purchase failed: ${err.message}`);
                setConfirmPending(null);
            })
            .finally(() => {
                setPurchaseLoading(false);
            });
    }, [confirmPending, shopState, mythrilBalance, setMythrilBalance]);

    const handleCancelConfirm = useCallback(() => {
        pendingRequestIdRef.current = null; // abandon this intent
        setConfirmPending(null);
    }, []);

    const handleCelebrationClose = useCallback(() => {
        setCelebrationOutcome(null);
    }, []);

    // --- Derived values ---
    const printerCost = getNextPrinterCost(shopState);
    const isLightweight = consecutivePulls > 1;

    // --- Live offer metadata for dynamic card titles ---
    const printerOffer = shopState?._offers?.find(o => o.offer_key === 'player_printer');
    const packOffer    = shopState?._offers?.find(o => o.offer_key === 'static_variant_pack');

    // --- Background style (image lives in public/art/ — must use process.env.PUBLIC_URL) ---
    const shopBgStyle = {
        background: [
            'linear-gradient(to bottom, rgba(17,17,17,0.30) 0%, rgba(17,17,17,0.85) 100%)',
            `url(${process.env.PUBLIC_URL}/art/image_51513d.jpg) center top / cover no-repeat`,
        ].join(', '),
    };

    // --- Render ---

    // Feature flag disabled — graceful "coming soon" state, not a crash screen
    if (shopDisabled) {
        return (
            <div className="shop-page shop-page--disabled">
                <div className="shop-disabled-message">
                    <h2>🧙 Wizard's Shop</h2>
                    <p>The Mythril Shop is coming soon. Check back after the next update!</p>
                    <Button variant="ghost" onClick={() => setPage('myTeams')}>
                        ← Back to My Teams
                    </Button>
                </div>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className="shop-page shop-page--loading">
                <div className="shop-loading-message">
                    <img src="/art/diamond_400_white_bg.svg" alt="Mythril" className="mythril-icon shop-loading-icon" />
                    Loading Shop...
                </div>
            </div>
        );
    }

    if (loadError) {
        return (
            <div className="shop-page shop-page--error">
                <div className="shop-error-message">
                    <p>Failed to load shop: {loadError}</p>
                    <Button variant="primary" onClick={() => window.location.reload()}>
                        Retry
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="shop-page" style={shopBgStyle}>
            {/* ================================================================
                Header Bar — Fix 5: title removed from here
                ================================================================ */}
            <div className="shop-header">
                {/* Back navigation */}
                <button className="shop-back-button" onClick={() => setPage('myTeams')}>
                    <span className="shop-back-arrow">◀</span>
                    <span className="shop-back-label">MY TEAMS</span>
                </button>

                {/* Right controls group */}
                <div className="shop-header-controls">
                    {/* Daily reset countdown */}
                    <div className="shop-countdown">
                        <span className="shop-countdown-label">Resets in</span>
                        <span className="shop-countdown-value">{countdown}</span>
                    </div>

                    {/* Quick Buy toggle — Fix 1: onChange now receives DOM event */}
                    <div className="shop-quickbuy-toggle">
                        <Checkbox
                            id="shop-quickbuy"
                            checked={quickBuy}
                            onChange={handleQuickBuyChange}
                            label="Quick Buy"
                        />
                    </div>

                    {/* Mythril balance — Fix 2: from AuthContext; Fix 10: diamond icon */}
                    <div className="shop-mythril-balance">
                        <img src="/art/diamond_400_white_bg.svg" alt="Mythril" className="mythril-icon shop-mythril-icon" />
                        <span className="shop-mythril-amount">
                            {mythrilBalance.toLocaleString()} Mythril
                        </span>
                        <Button
                            variant="ghost"
                            onClick={() => setShowMythrilModal(true)}
                            className="shop-buy-mythril-btn"
                        >
                            + Buy Mythril
                        </Button>
                    </div>
                </div>
            </div>

            {/* ================================================================
                Fix 5: Shop title placed here — above item tiles, below header
                ================================================================ */}
            <h1 className="shop-title">Mythic Shop</h1>

            {/* ================================================================
                Horizontal Item Lineup — Fix 6: center-justified via CSS
                ================================================================ */}
            <div className="shop-lineup">
                {/* 1. Random Card Printer */}
                <ShopItemCard
                    title={printerOffer?.display_name || 'Random Card Printer'}
                    description="Dispenses 1 random cosmetic variant card. Duplicates punch your card!"
                    cost={printerCost}
                    locked={shopState?.printerLocked}
                    comingSoon={false}
                    imageUrl="/art/card-printer.jpg"
                    onPurchase={() => handlePurchase('printer')}
                    confirmPending={confirmPending?.offerKey === 'printer'}
                    onConfirm={handleConfirm}
                    onCancelConfirm={handleCancelConfirm}
                    isLoading={purchaseLoading}
                >
                    <PunchCard punchCount={shopState?.punchCount || 0} />
                </ShopItemCard>

                {/* 2. Variant Pack — title always uses the set name "Variant Pack";
                    backend display_name may say "Static Variant Pack" which is the
                    internal offer key name, not the user-facing set name. */}
                <ShopItemCard
                    title="Variant Pack"
                    description={`Dispenses 5 static variant cards. ${shopState?.packNextPrice ?? VARIANT_PACK_COST} Mythril.`}
                    cost={shopState?.packNextPrice ?? VARIANT_PACK_COST}
                    locked={false}
                    comingSoon={false}
                    imageUrl="/art/ValuePackBackground.png"
                    onPurchase={() => handlePurchase('variantPack')}
                    confirmPending={confirmPending?.offerKey === 'variantPack'}
                    onConfirm={handleConfirm}
                    onCancelConfirm={handleCancelConfirm}
                    isLoading={purchaseLoading}
                />

                {/* 3. Omega Rare Pack — Coming Soon */}
                <ShopItemCard
                    title="Omega Rare Pack"
                    description="Premium gilded pack with rare variants."
                    cost={null}
                    locked={true}
                    comingSoon={true}
                    imageUrl="/art/GoldPackBackground.png"
                    onPurchase={() => {}}
                    confirmPending={false}
                    onConfirm={() => {}}
                    onCancelConfirm={() => {}}
                    isLoading={false}
                />

                {/* 4. Gear Pack — Coming Soon */}
                <ShopItemCard
                    title="Gear Pack"
                    description="Gear card cosmetic variants"
                    cost={null}
                    locked={true}
                    comingSoon={true}
                    imageUrl="/art/SilverPackBackground.png"
                    onPurchase={() => {}}
                    confirmPending={false}
                    onConfirm={() => {}}
                    onCancelConfirm={() => {}}
                    isLoading={false}
                />
            </div>

            {/* ================================================================
                Celebration Overlay
                ================================================================ */}
            {celebrationOutcome && (
                <CelebrationOverlay
                    outcome={celebrationOutcome}
                    onClose={handleCelebrationClose}
                    lightweight={isLightweight}
                />
            )}

            {/* ================================================================
                Mythril Purchase Modal
                ================================================================ */}
            <MythrilPurchaseModal
                isOpen={showMythrilModal}
                onClose={() => setShowMythrilModal(false)}
                onPurchase={handleMythrilPurchase}
            />
        </div>
    );
}

export default ShopPage;
